import moment from "moment";

/**
 * Boards, timeline, and period summaries roll over on COMPANY time (Pacific),
 * not the viewer's browser clock. Before this, a viewer in a later timezone
 * (e.g. UTC+8) saw Jul 31 Pacific scores display as Aug 1 and boards reset
 * hours before the company's month actually ended.
 */
export const COMPANY_TIMEZONE = "America/Los_Angeles";

/** "YYYY-MM-DD" for today's date in company time. */
export function companyTodayStr() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
  }).format(new Date());
}

/**
 * Moment at midnight of the company-local today. Comparable against
 * moment("YYYY-MM-DD") day keys, which also parse as local midnight.
 */
export function companyToday() {
  return moment(companyTodayStr(), "YYYY-MM-DD");
}

/** "YYYY-MM" for the current month in company time. */
export function companyMonthStr() {
  return companyTodayStr().slice(0, 7);
}

/** "YYYY-MM-DD" for an instant in company time — use for display + filtering. */
export function companyDayKey(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
  }).format(new Date(iso));
}

/** e.g. "Jul 31" or "31 Jul 2026" (no en-US comma in year form). */
export function formatCompanyDate(iso, { month = "short", day = "numeric", year } = {}) {
  if (!iso) return "";
  const opts = { timeZone: COMPANY_TIMEZONE, month, day };
  if (year) opts.year = year;
  /* en-US prints "Jul 31, 2026"; boards / range chips want no comma. */
  return new Intl.DateTimeFormat(year ? "en-GB" : "en-US", opts).format(new Date(iso));
}

const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Setup date inputs — Chrome/Windows often paints native <input type="date">
 * as ", January, 01, 2026" (empty weekday + commas). Paint our own label instead.
 * `ymd` is "YYYY-MM-DD" (calendar day, not a timezone-shifted Instant).
 */
export function formatSetupDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || "").slice(0, 10));
  if (!m) return "";
  const month = MONTHS_LONG[Number(m[2]) - 1];
  if (!month) return "";
  return `${month} ${Number(m[3])} ${m[1]}`;
}

/** e.g. "12:00 PM" */
export function formatCompanyTime(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: COMPANY_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
}

/** e.g. "31 Jul 2026 at 12:00 PM" */
export function formatCompanyDateTime(iso) {
  if (!iso) return "";
  return `${formatCompanyDate(iso, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })} at ${formatCompanyTime(iso)}`;
}

/** Calendar day string for a Date/moment picked in the date filter UI. */
export function companyDayKeyFromPicker(value) {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  if (value.format) return value.format("YYYY-MM-DD");
  return companyDayKey(value);
}
